import PathToRegexp from 'path-to-regexp';
import { once } from '@curi/helpers';

function registerRoutes(routes, interaction, parentData) {
    routes.forEach(function (route) {
        var data = interaction.register(route.public, parentData);
        registerRoutes(route.children, interaction, data);
    });
}

var withLeadingSlash = function (path) {
    return path.charAt(0) === "/" ? path : "/" + path;
};
var withTrailingSlash = function (path) {
    return path.charAt(path.length - 1) === "/" ? path : path + "/";
};
var join = function (beginning, end) {
    return withTrailingSlash(beginning) + end;
};

function generatePathname(options) {
    var knownPaths = {};
    var cache = {};
    var alreadyCompiled = {};
    return {
        name: "pathname",
        register: function (route, parent) {
            var name = route.name, path = route.path, pathname = route.pathname;
            var base;
            if (parent && knownPaths[parent]) {
                base = knownPaths[parent];
            }
            knownPaths[name] = withLeadingSlash(base ? join(base, path) : path);
            if (pathname) {
                cache[name] = pathname;
            }
            return name;
        },
        get: function (name, params) {
            if (knownPaths[name] == null) {
                if (process.env.NODE_ENV !== "production") {
                    console.error("Could not generate pathname for " + name + " because it is not registered.");
                }
                return;
            }
            var hash = "" + name + JSON.stringify(params);
            if (alreadyCompiled[hash]) {
                return alreadyCompiled[hash];
            }
            var compile = cache[name]
                ? cache[name]
                : (cache[name] = PathToRegexp.compile(knownPaths[name]));
            var output = compile(params, options);
            alreadyCompiled[hash] = output;
            return output;
        },
        reset: function () {
            knownPaths = {};
            cache = {};
            alreadyCompiled = {};
        }
    };
}

function createRedirect(redirectTo, interactions, history) {
    var name = redirectTo.name, params = redirectTo.params, query = redirectTo.query, hash = redirectTo.hash, state = redirectTo.state;
    var pathname = interactions.pathname(name, params);
    return {
        name: name,
        params: params,
        pathname: pathname,
        query: query,
        hash: hash,
        state: state,
        url: history.toHref({ pathname: pathname, query: query, hash: hash, state: state })
    };
}
function finishResponse(routeMatch, interactions, resolvedResults, history, external) {
    var route = routeMatch.route, match = routeMatch.match;
    var response = match;
    if (!route.response) {
        return response;
    }
    var _a = resolvedResults || {}, _b = _a.resolved, resolved = _b === void 0 ? null : _b, _c = _a.error, error = _c === void 0 ? null : _c;
    var responseModifiers = route.response({
        resolved: resolved,
        error: error,
        match: match,
        external: external
    });
    if (!responseModifiers) {
        return response;
    }
    var settableProperties = ["status", "error", "body", "data", "title", "redirectTo"];
    // only merge the valid properties onto the response
    settableProperties.forEach(function (p) {
        if (responseModifiers.hasOwnProperty(p)) {
            if (p === "redirectTo") {
                // special case
                response[p] = createRedirect(responseModifiers[p], interactions, history);
            }
            else {
                response[p] = responseModifiers[p];
            }
        }
    });
    return response;
}

function parseParams(params, fns) {
    if (!fns) {
        return params;
    }
    var output = {};
    // For each param, attempt to parse it. However, if that
    // fails, fall back to the string value.
    for (var key in params) {
        var value = params[key];
        var fn = fns[key];
        if (fn) {
            try {
                value = fn(value);
            }
            catch (e) {
                console.error(e);
                value = params[key];
            }
        }
        output[key] = value;
    }
    return output;
}

function matchRoute(route, pathname) {
    var testPath = pathname;
    var regExpMatch = route.pathMatching.re.exec(testPath);
    if (!regExpMatch) {
        return [];
    }
    var matchedSegment = regExpMatch[0];
    var parsed = regExpMatch.slice(1);
    var params = {};
    route.pathMatching.keys.forEach(function (key, index) {
        params[key.name] = parsed[index];
    });
    var matches = [{ route: route, params: params }];
    // if there are no children routes, immediately accept the match
    if (!route.children.length) {
        return matches;
    }
    // children only need to match against unmatched segments
    var remainingSegments = testPath.slice(matchedSegment.length);
    if (remainingSegments !== "") {
        var childrenLength = route.children.length;
        // a parent that ends with a slash will have stripped the leading
        // slash from remaining segments, so re-add it
        var fullSegments = withLeadingSlash(remainingSegments);
        for (var i = 0; i < childrenLength; i++) {
            var matched = matchRoute(route.children[i], fullSegments);
            if (matched.length) {
                matches = matches.concat(matched);
                break;
            }
        }
    }
    // return matches if a child route matches or this route matches exactly
    return matches.length > 1 ||
        (route.pathMatching.mustBeExact && remainingSegments.length === 0)
        ? matches
        : [];
}
function createMatch(routeMatches, location) {
    var partials = [];
    var params = {};
    var bestMatch = routeMatches.pop();
    // handle ancestor routes
    routeMatches.forEach(function (match) {
        partials.push(match.route.public.name);
        Object.assign(params, parseParams(match.params, match.route.paramParsers));
    });
    // handle best match
    Object.assign(params, parseParams(bestMatch.params, bestMatch.route.paramParsers));
    return {
        route: bestMatch.route,
        match: {
            location: location,
            name: bestMatch.route.public.name,
            params: params,
            partials: partials
        }
    };
}
function matchLocation(location, routes) {
    // determine which route(s) match, then use the exact match
    // as the matched route and the rest as partial routes
    var routeLength = routes.length;
    for (var i = 0; i < routeLength; i++) {
        var routeMatches = matchRoute(routes[i], location.pathname);
        if (routeMatches.length) {
            return createMatch(routeMatches, location);
        }
    }
    // no matching route
    return {
        route: undefined,
        match: undefined
    };
}

/*
 * This will call any initial/every match functions for the matching route
 */
function resolveRoute(match, global) {
    var resolve = match.route.public.resolve;
    var _a = Object.keys(resolve).reduce(function (acc, key) {
        acc.keys.push(key);
        acc.promises.push(resolve[key](match.match, global));
        return acc;
    }, { keys: [], promises: [] }), keys = _a.keys, promises = _a.promises;
    return Promise.all(promises).then(function (results) {
        return {
            resolved: results.reduce(function (acc, curr, index) {
                acc[keys[index]] = curr;
                return acc;
            }, {}),
            error: null
        };
    }, function (error) { return ({ error: error, resolved: null }); });
}

var createRoute = function (options, parentPath, usedNames) {
    if (usedNames.has(options.name)) {
        throw new Error("Multiple routes have the name \"" + options.name + "\". Route names must be unique.");
    }
    usedNames.add(options.name);
    var path = options.path;
    if (path.charAt(0) === "/") {
        if (process.env.NODE_ENV !== "production") {
            console.warn("Route paths cannot start with a forward slash (/). (Received \"" + path + "\")");
        }
        path = path.slice(1);
    }
    var fullPath = withLeadingSlash(join(parentPath || "", path));
    var pathOptions = options.pathOptions || {};
    // end defaults to true, so end has to be hardcoded for it to be false
    // set this resolve setting pathOptions.end for children
    var mustBeExact = pathOptions.end == null || pathOptions.end;
    var children = [];
    // when we have child routes, we need to perform non-end matching and
    // create route objects for each child
    if (options.children && options.children.length) {
        pathOptions.end = false;
        children = options.children.map(function (child) {
            return createRoute(child, fullPath, usedNames);
        });
    }
    // keys is populated by PathToRegexp
    var keys = [];
    // path is compiled with a leading slash
    // for optional initial params
    var re = PathToRegexp(withLeadingSlash(path), keys, pathOptions);
    var pathname = PathToRegexp.compile(fullPath);
    var resolve = options.resolve || {};
    return {
        public: {
            name: options.name,
            path: path,
            keys: keys.map(function (key) { return key.name; }),
            resolve: resolve,
            extra: options.extra,
            pathname: pathname
        },
        pathMatching: {
            re: re,
            keys: keys,
            mustBeExact: mustBeExact
        },
        sync: !Object.keys(resolve).length,
        response: options.response,
        children: children,
        paramParsers: options.params
    };
};

function prepareRoutes(userRoutes) {
    return privatePrepareRoutes(userRoutes);
}
function privatePrepareRoutes(userRoutes, _privateInternalCall) {
    if (_privateInternalCall === void 0) { _privateInternalCall = false; }
    var hasWarned = false;
    var usedNames = new Set();
    return userRoutes.map(function (route) {
        if (route.public !== undefined) {
            return route;
        }
        if (process.env.NODE_ENV !== "production") {
            if (_privateInternalCall && !hasWarned) {
                console.warn("Deprecation Warning:\nYou passed a plain array to your curi() call. This will be removed in the next major version. Instead, you should pass a compiled routes array.\n\nimport { curi, prepareRoutes } from \"@curi/router\";\n\nconst routes = prepareRoutes([...]);\nconst router = curi(history, routes);");
                hasWarned = true;
            }
        }
        return createRoute(route, null, usedNames);
    });
}

function createRouter(history, routeArray, options) {
    if (options === void 0) { options = {}; }
    var _a = options.route, userInteractions = _a === void 0 ? [] : _a, _b = options.sideEffects, sideEffects = _b === void 0 ? [] : _b, _c = options.emitRedirects, emitRedirects = _c === void 0 ? true : _c, _d = options.automaticRedirects, automaticRedirects = _d === void 0 ? true : _d, external = options.external;
    var routes;
    var routeInteractions = {};
    // the navigation currently being processed
    var activeNavigation;
    // the last finish response & navigation
    var mostRecent = {
        response: null,
        navigation: null
    };
    // when true, navigation.previous will re-use the previous
    // navigation.previous instead of using mostRecent.response
    var refreshing = false;
    // router.navigate() hooks
    var cancelCallback;
    var finishCallback;
    var observers = [];
    var oneTimers = [];
    var cancellers = [];
    function setupRoutesAndInteractions(userRoutes) {
        if (userRoutes) {
            routes = privatePrepareRoutes(userRoutes, true);
            for (var key in routeInteractions) {
                delete routeInteractions[key];
            }
            // add the pathname interaction to the provided interactions
            userInteractions
                .concat(generatePathname(options.pathnameOptions))
                .forEach(function (interaction) {
                interaction.reset();
                routeInteractions[interaction.name] = interaction.get;
                registerRoutes(routes, interaction);
            });
        }
        // assign navigation response handler
        // this will be re-called if router.refresh() is called
        history.respondWith(navigationHandler);
    }
    function navigationHandler(pendingNav) {
        cancelActiveNavigation(pendingNav.action);
        activeNavigation = pendingNav;
        var navigation = {
            action: pendingNav.action,
            previous: refreshing
                ? mostRecent.navigation
                    ? mostRecent.navigation.previous
                    : null
                : mostRecent.response
        };
        refreshing = false;
        var match = matchLocation(pendingNav.location, routes);
        // if no routes match, do nothing
        if (!match.route) {
            if (process.env.NODE_ENV !== "production") {
                console.error("The current location (" + pendingNav.location.pathname + ") has no matching route, " +
                    'so a response could not be emitted. A catch-all route ({ path: "(.*)" }) ' +
                    "can be used to match locations with no other matching route.");
            }
            pendingNav.finish();
            if (finishCallback) {
                finishCallback();
            }
            resetCallbacks();
            activeNavigation = undefined;
            return;
        }
        if (match.route.sync) {
            finalizeResponseAndEmit(match, pendingNav, navigation, null);
        }
        else {
            activateCancellers(pendingNav.action);
            resolveRoute(match, external).then(function (resolved) {
                if (pendingNav.cancelled) {
                    return;
                }
                finalizeResponseAndEmit(match, pendingNav, navigation, resolved);
            });
        }
    }
    function finalizeResponseAndEmit(match, pending, navigation, resolved) {
        deactivateCancellers();
        var response = finishResponse(match, routeInteractions, resolved, history, external);
        pending.finish();
        emitImmediate(response, navigation);
        activeNavigation = undefined;
    }
    function resetCallbacks() {
        cancelCallback = undefined;
        finishCallback = undefined;
    }
    function callObservers(emitted) {
        observers.forEach(function (fn) {
            fn(emitted);
        });
    }
    function callOneTimersAndSideEffects(emitted) {
        oneTimers.splice(0).forEach(function (fn) {
            fn(emitted);
        });
        sideEffects.forEach(function (fn) {
            fn(emitted);
        });
    }
    function emitImmediate(response, navigation) {
        if (finishCallback) {
            finishCallback();
        }
        resetCallbacks();
        if (!response.redirectTo || emitRedirects) {
            // store for current(), observe(), and once()
            mostRecent.response = response;
            mostRecent.navigation = navigation;
            callObservers({ response: response, navigation: navigation, router: router });
            callOneTimersAndSideEffects({ response: response, navigation: navigation, router: router });
        }
        if (response.redirectTo !== undefined && automaticRedirects) {
            history.navigate(response.redirectTo, "REPLACE");
        }
    }
    var canCancel;
    function activateCancellers(action) {
        if (cancellers.length) {
            canCancel = true;
            var cancel_1 = function () {
                cancelActiveNavigation(action);
                deactivateCancellers();
                if (cancelCallback) {
                    cancelCallback();
                }
                resetCallbacks();
            };
            cancellers.forEach(function (fn) {
                fn(cancel_1);
            });
        }
    }
    function deactivateCancellers() {
        if (canCancel) {
            canCancel = false;
            cancellers.forEach(function (fn) {
                fn();
            });
        }
    }
    function cancelActiveNavigation(action) {
        if (activeNavigation) {
            activeNavigation.cancel(action);
            activeNavigation.cancelled = true;
        }
    }
    var router = {
        route: routeInteractions,
        history: history,
        observe: function (fn, options) {
            var _a = (options || {}).initial, initial = _a === void 0 ? true : _a;
            observers.push(fn);
            if (mostRecent.response && initial) {
                fn.call(null, {
                    response: mostRecent.response,
                    navigation: mostRecent.navigation,
                    router: router
                });
            }
            return function () {
                observers = observers.filter(function (obs) {
                    return obs !== fn;
                });
            };
        },
        once: function (fn, options) {
            var _a = (options || {}).initial, initial = _a === void 0 ? true : _a;
            if (mostRecent.response && initial) {
                fn.call(null, {
                    response: mostRecent.response,
                    navigation: mostRecent.navigation,
                    router: router
                });
            }
            else {
                oneTimers.push(fn);
            }
        },
        cancel: function (fn) {
            cancellers.push(fn);
            return function () {
                cancellers = cancellers.filter(function (can) {
                    return can !== fn;
                });
            };
        },
        refresh: function (routes) {
            refreshing = true;
            setupRoutesAndInteractions(routes);
        },
        current: function () {
            return mostRecent;
        },
        navigate: function (details) {
            if (cancelCallback) {
                cancelCallback();
            }
            resetCallbacks();
            var name = details.name, params = details.params, hash = details.hash, query = details.query, state = details.state, _a = details.method, method = _a === void 0 ? "ANCHOR" : _a;
            var pathname = name != null
                ? routeInteractions.pathname(name, params)
                : history.location.pathname;
            if (method !== "ANCHOR" && method !== "PUSH" && method !== "REPLACE") {
                method = "ANCHOR";
            }
            cancelCallback = details.cancelled;
            finishCallback = details.finished;
            history.navigate({
                pathname: pathname,
                hash: hash,
                query: query,
                state: state
            }, method);
        }
    };
    // now that everything is defined, actually do the setup
    setupRoutesAndInteractions(routeArray);
    return router;
}

function deprecatedPathname(options) {
    var hasWarned = false;
    if (process.env.NODE_ENV !== "production") {
        if (!hasWarned) {
            console.warn("Deprecation warning:\npathname() should not be called manually. It will be removed in the next major version of @curi/router.\nPlease use router.route.pathname() instead.\n\nconst router = curi(history, routes);\nrouter.route.pathname(\"Route Name\");");
            hasWarned = true;
        }
    }
    return generatePathname(options);
}

var hasWarned = false;
function deprecatedOnce(fn) {
    if (process.env.NODE_ENV !== "production") {
        if (!hasWarned) {
            console.warn("Deprecation warning:\nonce() has been moved to the @curi/helpers package.\nPlease install @curi/helpers and import once() from that package instead.\n\nimport { once } from \"@curi/helpers\";");
            hasWarned = true;
        }
    }
    return once(fn);
}

export { createRouter as curi, deprecatedPathname as pathname, deprecatedOnce as once, prepareRoutes };



//////////////////
// WEBPACK FOOTER
// ./node_modules/@curi/router/dist/curi-router.es.js
// module id = 307
// module chunks = 0